import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useHouses } from "@/hooks/useHouses";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import StatsGrid from "@/components/dashboard/StatsGrid";
import ExpensesChart from "@/components/dashboard/ExpensesChart";
import RecentActivity from "@/components/dashboard/RecentActivity";
import { useExpensesData, useMonthlyExpenses } from "@/components/dashboard/useExpensesData";
import { Tables } from "@/integrations/supabase/types";

type Payment = Tables<"payments">;

const Dashboard = () => {
  const { houses } = useHouses();
  const { totalWaterExpense, totalElectricityExpense } = useExpensesData(houses);
  const numericWaterExpense = Number(totalWaterExpense) || 0;
  const numericElectricityExpense = Number(totalElectricityExpense) || 0;
  const { data: monthlyExpenses = [] } = useMonthlyExpenses(houses);

  // Fetch pending payments
  const { data: pendingPayments = [] } = useQuery<Payment[]>({
    queryKey: ['pending-payments', houses],
    queryFn: async () => {
      if (!houses.length) return [];

      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .in('house_id', houses.map(house => house.id))
        .eq('status', 'pending');

      if (error) throw error;
      return data || [];
    },
    enabled: houses.length > 0,
  });

  // Fetch pending readings
  const { data: pendingReadings = [] } = useQuery({
    queryKey: ['pending-readings', houses],
    queryFn: async () => {
      if (!houses.length) return [];

      const { data, error } = await supabase
        .from('readings')
        .select('*')
        .in('house_id', houses.map(house => house.id))
        .eq('status', 'pending');

      if (error) throw error;
      return data || [];
    },
    enabled: houses.length > 0,
  });

  return (
    <div className="flex flex-col px-4 pt-4 max-w-[1600px] mx-auto h-full">
      <DashboardHeader />
      
      <div className="mt-4">
        <StatsGrid
          waterExpense={numericWaterExpense}
          electricityExpense={numericElectricityExpense}
          pendingPayments={pendingPayments.length}
          pendingReadings={pendingReadings.length}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4 h-[calc(100vh-320px)] mb-6 lg:mb-0">
        <ExpensesChart data={monthlyExpenses} />
        <RecentActivity payments={pendingPayments} />
      </div>
    </div>
  );
};

export default Dashboard;